<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import SortableList from '@js/components/lists/SortableList.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { computed, reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const types = [
    { value: 'others', label: 'Autres' },
    { value: 'opinions', label: 'Avis' },
    { value: 'flow_manager', label: 'Gestionnaire de flux' },
    { value: 'delivery', label: 'Livraison' },
    { value: 'netlinking', label: 'Netlinking' },
    { value: 'payment', label: 'Paiement' },
];

const { affiliation } = defineProps({
    affiliation: {
        required: false,
        type: Object,
    },
});

const { route } = useRoute();

let contactUidCounter = 0;

function nextContactUid() {
    return `${++contactUidCounter}`;
}

const form = reactive({
    name: affiliation ? affiliation.name : '',
    url: affiliation ? affiliation.url : null,
    type: affiliation
        ? types.find((type) => type.value === affiliation.type)
        : types[0],
    isArchived: affiliation ? affiliation.isArchived : false,
    internalNotes: affiliation ? affiliation.internalNotes : null,
    contacts:
        affiliation && affiliation.contacts && affiliation.contacts.length > 0
            ? affiliation.contacts.map((contact) => ({
                  ...contact,
                  _uid: nextContactUid(),
              }))
            : [],
});

const isSubmitting = ref(false);

const baseValidationSchema = object({
    name: string().required('Ce champ est obligatoire.'),
    url: string().url('Veuillez saisir une URL valide.').nullable(),
    type: object({
        value: string().required('Ce champ est obligatoire.'),
        label: string().required('Ce champ est obligatoire.'),
    }).required('Ce champ est obligatoire.'),
    isArchived: boolean().required('Ce champ est obligatoire.'),
    internalNotes: string().nullable(),
});

const validationSchema = computed(() => {
    const dynamicSchema = {};

    form.contacts.forEach((contact) => {
        dynamicSchema[`contactName_${contact._uid}`] = string().required(
            'Ce champ est obligatoire.',
        );
        dynamicSchema[`contactEmail_${contact._uid}`] = string()
            .email('Veuillez saisir une adresse email valide.')
            .nullable();
    });

    return baseValidationSchema.shape(dynamicSchema);
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            affiliation
                ? route('front.projectManagement.apikom.postAffiliation', {
                      affiliation: affiliation.id,
                  })
                : route('front.projectManagement.apikom.postAffiliation'),
            {
                name: form.name,
                url: form.url,
                type: form.type.value,
                isArchived: form.isArchived,
                internalNotes: form.internalNotes,
                contacts: form.contacts.map((contact) => ({
                    id: contact.id ?? null,
                    name: contact.name,
                    email: contact.email,
                    phone: contact.phone,
                })),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(route('front.projectManagement.apikom.affiliations'));
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(route('front.projectManagement.apikom.affiliations'));
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    forceMount: true,
                    contentSlotName: 'information',
                },
                internalNotes: {
                    title: 'Notes internes',
                    forceMount: true,
                    contentSlotName: 'internalNotes',
                },
            }"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="3">
                            <InputField
                                v-model="form.name"
                                label="Nom"
                                field-name="name"
                                field-id="name"
                                type="text"
                                required
                            />

                            <InputField
                                v-model="form.url"
                                label="URL"
                                field-name="url"
                                field-id="url"
                                type="url"
                            />

                            <ListboxField
                                v-model="form.type"
                                field-id="type"
                                field-name="type"
                                label="Type"
                                :options="types"
                                required
                            >
                                <template #buttonContent>
                                    {{ form.type.label }}
                                </template>

                                <template #optionContent="{ option }">
                                    {{ option.label }}
                                </template>
                            </ListboxField>
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <SwitchField
                            v-model="form.isArchived"
                            label="Archivé"
                            field-name="isArchived"
                            field-id="isArchived"
                        />
                    </FormContent>

                    <FormContent>
                        <SortableList
                            v-model="form.contacts"
                            :sortable="false"
                            add-label="Ajouter un contact"
                            empty-text="Aucun contact pour le moment."
                            :new-item="
                                () => ({
                                    id: null,
                                    name: '',
                                    email: '',
                                    phone: '',
                                    _uid: nextContactUid(),
                                })
                            "
                        >
                            <template #default="{ item }">
                                <FormGrid :columns="3">
                                    <InputField
                                        v-model="item.name"
                                        label="Nom"
                                        :field-name="`contactName_${item._uid}`"
                                        :field-id="`contactName_${item._uid}`"
                                        type="text"
                                        required
                                    />

                                    <InputField
                                        v-model="item.email"
                                        label="Email"
                                        :field-name="`contactEmail_${item._uid}`"
                                        :field-id="`contactEmail_${item._uid}`"
                                        type="email"
                                    />

                                    <InputField
                                        v-model="item.phone"
                                        label="Téléphone"
                                        :field-name="`contactPhone_${item._uid}`"
                                        :field-id="`contactPhone_${item._uid}`"
                                        type="tel"
                                    />
                                </FormGrid>
                            </template>
                        </SortableList>
                    </FormContent>
                </FormColumn>
            </template>

            <template #internalNotes>
                <FormColumn>
                    <FormContent>
                        <EditorField
                            v-model="form.internalNotes"
                            label="Notes internes"
                            field-name="internalNotes"
                            field-id="internalNotes"
                        />
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="affiliation"
                    :url="
                        route(
                            'front.projectManagement.apikom.deleteAffiliation',
                            {
                                affiliation: affiliation.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.apikom.affiliations',
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
